import { DatePicker, Form, Input, Modal, Typography, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { mapStarError } from 'pages/star-shared/star-error';
import { formatNumber, parseKcInput } from 'pages/star-shared/utils';
import UserPicker, { UserPickerValue } from 'pages/star-shared/components/user-picker';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValue {
  user?: UserPickerValue;
  kcDate?: Dayjs;
  kc?: string;
  note?: string;
}

const NOTE_MIN = 5;
const NOTE_MAX = 255;

/**
 * Ngày `day` (YYYY-MM-DD) chưa tới theo ngày TikTok (UTC+0)? Máy ở giờ Việt Nam từ 00:00 đến
 * 06:59 đã sang ngày mới trong khi ngày TikTok đó chưa bắt đầu, nên không so với ngày của máy.
 */
export const isFutureTiktokDay = (day: string, now: Date = new Date()) =>
  day > now.toISOString().slice(0, 10);

// Sửa tay KC 1 ngày: POST /stars/admin/kc/manual { userId, kcDate, kc, note } → chấm ngay.
// Mở từ trang Nhập dữ liệu (nút "Sửa tay 1 dòng"). `note` = lý do, bắt buộc; server ghi vào
// Lịch sử nhập (data_import_logs, loại MANUAL). Chưa được cấp quyền nhập → 409.
const KcManualModal = ({ open, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm<FormValue>();
  const [loading, setLoading] = useState(false);
  const kcRaw = Form.useWatch('kc', form);
  const parsed = parseKcInput(kcRaw);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        user: undefined,
        kcDate: dayjs().subtract(1, 'day'),
        kc: '',
        note: '',
      });
    }
  }, [open, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: FormValue) => {
    const kc = parseKcInput(values.kc);
    const note = (values.note || '').trim();
    if (!values.user?.value || !values.kcDate || kc === null || note.length < NOTE_MIN) return;
    setLoading(true);
    try {
      const res: any = await AxiosUtils.post(API.starsKcManual, {
        userId: values.user.value,
        kcDate: values.kcDate.format('YYYY-MM-DD'),
        kc,
        note,
      });
      const evaluation = res?.data?.evaluation;
      const given =
        typeof evaluation?.starGiven === 'number'
          ? evaluation.starGiven
          : typeof evaluation?.stars === 'number'
          ? evaluation.stars
          : undefined;
      notification.success({
        message: `Đã ghi ${formatNumber(kc)} KC ngày ${values.kcDate.format('DD/MM/YYYY')}.`,
        description:
          typeof given === 'number'
            ? `Kết quả chấm: ${given >= 0 ? '+' : ''}${formatNumber(given)} ⭐${
                typeof evaluation?.tierStar === 'number' ? ` (mốc ${formatNumber(evaluation.tierStar)} ⭐)` : ''
              }`
            : undefined,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Sửa tay Kim cương 1 ngày"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={520}
    >
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        <Form.Item
          label="Creator (gồm Creator NPC)"
          name="user"
          rules={[{ required: true, message: 'Vui lòng chọn người.' }]}
          extra="Không nhập cho chính mình. Người này cần có TikTok UID trong hồ sơ."
        >
          <UserPicker />
        </Form.Item>
        <Form.Item
          label="Ngày (theo ngày TikTok)"
          name="kcDate"
          rules={[
            { required: true, message: 'Chọn ngày.' },
            {
              // Form tự chặn ngày chưa tới, không trông vào việc máy chủ có chặn hay không.
              validator: (_, value?: Dayjs) =>
                value && isFutureTiktokDay(value.format('YYYY-MM-DD'))
                  ? Promise.reject(new Error('Ngày này chưa tới theo giờ TikTok (UTC+0).'))
                  : Promise.resolve(),
            },
          ]}
          extra="Ngày TikTok tính theo giờ UTC+0 (07:00 sáng giờ Việt Nam mới sang ngày mới)."
        >
          <DatePicker
            format="DD/MM/YYYY"
            disabledDate={(d) => isFutureTiktokDay(d.format('YYYY-MM-DD'))}
          />
        </Form.Item>
        <Form.Item
          label="Kim cương (KC)"
          name="kc"
          required
          rules={[
            {
              validator: (_, value) => {
                const kc = parseKcInput(value);
                return kc !== null && kc >= 0
                  ? Promise.resolve()
                  : Promise.reject(new Error('Nhập số KC, ví dụ 150000 hoặc 150.000.'));
              },
            },
          ]}
          extra={
            parsed !== null ? (
              <Typography.Text type="secondary">= {formatNumber(parsed)} KC</Typography.Text>
            ) : (
              'Chấp nhận "150000" hoặc "150.000".'
            )
          }
        >
          <Input placeholder="150.000" inputMode="numeric" maxLength={15} />
        </Form.Item>
        <Form.Item
          label="Lý do"
          name="note"
          required
          rules={[
            {
              validator: (_, value) => {
                const length = String(value || '').trim().length;
                return length >= NOTE_MIN && length <= NOTE_MAX
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(`Vui lòng ghi lý do, từ ${NOTE_MIN} đến ${NOTE_MAX} ký tự.`)
                    );
              },
            },
          ]}
          extra="Lý do được lưu trong Lịch sử nhập để Admin đối chiếu."
        >
          <Input.TextArea
            rows={2}
            maxLength={NOTE_MAX}
            showCount
            placeholder="File TikTok thiếu dòng của Creator này"
          />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton text="Ghi & chấm" bgColor="secondary-green" htmlType="submit" loading={loading} />
        </div>
      </Form>
    </Modal>
  );
};

export default KcManualModal;
